import { useEffect, useRef, useState } from 'react';
import { baixarBlob } from '../../api';
import {
  alterarFoto, enviarFotosDaVertical, type FotoDaVertical, type VerticalCompleta,
} from '../../api/cptu';
import { plural } from '../../formato';
import {
  Area, BarraDeAvanco, Carregando, Dialogo, ErroDaApi, FotoProtegida, ZonaDeArquivo,
} from '../../ui';

/**
 * As fotos da vertical — o equipamento no ponto (spec 07 §4; Tarefa 312.5).
 *
 * As miniaturas vêm pela `FotoProtegida` (o arquivo exige sessão). Quem
 * edita envia pela zona de arquivo ou fotografa na hora (`capture` abre a
 * câmera do celular), escreve a legenda e diz se a foto vai ao relatório.
 * Tirar a foto da vertical não apaga nada: a foto e o arquivo ficam, e ela
 * aparece em «Fotos retiradas», de onde volta.
 *
 * Os envios vão em lotes de `LIMITE_POR_ENVIO` (8): é o `LIMITE_FOTOS` da
 * rota (`api/src/cptu/verticais.controller.ts`); da nona em diante a API
 * recusaria o pedido inteiro. A HEIC do iPhone é aceita pela API; o
 * navegador que não a desenha mostra o nome do arquivo no lugar.
 *
 * A miniatura é um botão (teclado e leitor de tela): abre a foto numa janela,
 * na largura dela e cabendo na tela, com «Baixar» — também no celular, onde a
 * campanha é consulta. A HEIC, que o navegador não desenha, abre só com o
 * «Baixar» (agent-browser da 312).
 */

/** O limite de fotos por pedido da rota `POST /cptu/verticais/:id/fotos` (`LIMITE_FOTOS`). */
const LIMITE_POR_ENVIO = 8;

const ACEITA = 'image/*,.heic,.heif';

export function FotosDaVertical({ vertical, editavel, aoMudar }: {
  vertical: VerticalCompleta;
  /** A campanha muda (rascunho ou devolvida) e a pessoa tem `cptu.editar`. */
  editavel: boolean;
  aoMudar(v: VerticalCompleta): Promise<void>;
}) {
  const [erro, setErro] = useState<unknown>(null);
  const [progresso, setProgresso] = useState<number | null>(null);
  const [tirando, setTirando] = useState<FotoDaVertical | null>(null);
  const [vendo, setVendo] = useState<FotoDaVertical | null>(null);
  const camera = useRef<HTMLInputElement>(null);
  const fotos = vertical.fotos;
  const retiradas = vertical.fotos_retiradas ?? [];

  const enviar = async (arquivos: File[]) => {
    if (!arquivos.length) return;
    setErro(null); setProgresso(0);
    const lotes: File[][] = [];
    for (let i = 0; i < arquivos.length; i += LIMITE_POR_ENVIO) {
      lotes.push(arquivos.slice(i, i + LIMITE_POR_ENVIO));
    }
    let ultima: VerticalCompleta | null = null;
    try {
      for (let l = 0; l < lotes.length; l++) {
        const fd = new FormData();
        for (const f of lotes[l]) fd.append('fotos', f);
        ultima = await enviarFotosDaVertical(vertical.id, fd, (fr) => setProgresso((l + fr) / lotes.length));
      }
    } catch (e) { setErro(e); } finally {
      setProgresso(null);
      /* O lote que entrou antes da falha aparece: a tela não esconde foto que já está gravada. */
      if (ultima) await aoMudar(ultima);
    }
  };

  const mudar = async (f: FotoDaVertical, corpo: Parameters<typeof alterarFoto>[1]) => {
    setErro(null);
    try { await aoMudar(await alterarFoto(f.id, corpo)); } catch (e) { setErro(e); }
  };

  return (
    <div className="cptu-fotos">
      <h3>Fotos do equipamento no ponto</h3>
      <ErroDaApi erro={erro} />
      {fotos.length === 0 ? (
        <p className="sub">
          Sem foto. {editavel ? 'Fotografe o equipamento e o ponto: a foto marcada «no relatório» vai ao relatório da campanha.' : ''}
        </p>
      ) : (
        <ul className="cptu-fotos-grade">
          {fotos.map((f) => (
            <li key={f.id} className="cptu-foto" data-foto={f.id}>
              <button type="button" className="cptu-foto-abrir" onClick={() => setVendo(f)}
                aria-label={`Ver a foto «${f.arquivo}» maior`}>
                <FotoProtegida arquivoId={f.arquivo_id} alt={f.legenda ?? `foto da vertical ${vertical.codigo}`}
                  carregando={<span className="cptu-foto-carregando">…</span>}
                  semImagem={(
                    <span className="cptu-foto-nome">
                      <span className="mono">{f.arquivo}</span>
                      <span>O navegador não abre este formato: a foto está guardada como veio.</span>
                    </span>
                  )} />
              </button>
              {editavel ? (
                <>
                  <LegendaDaFoto foto={f} aoGravar={(legenda) => mudar(f, { legenda })} />
                  <label className="cptu-foto-relatorio">
                    <input type="checkbox" checked={f.no_relatorio}
                      onChange={(e) => { void mudar(f, { noRelatorio: e.target.checked }); }} />
                    No relatório
                  </label>
                  {f.no_relatorio && ehHeic(f) && <AvisoDoHeic foto={f} />}
                  <button type="button" className="ligacao" onClick={() => setTirando(f)}>
                    Tirar da vertical
                  </button>
                </>
              ) : (
                <>
                  <p className="cptu-foto-legenda">
                    {f.legenda ?? <span className="nada">sem legenda</span>}
                    <span className="sub">{f.no_relatorio ? 'no relatório' : 'fora do relatório'}</span>
                  </p>
                  {f.no_relatorio && ehHeic(f) && <AvisoDoHeic foto={f} />}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {editavel && (
        <div className="cptu-fotos-envio">
          {/*
            * A câmera num `<input capture>` próprio, e a zona para o arquivo que já existe — o molde
            * das fotos do equipamento: no celular `capture` abre a câmera direto; no computador ele não
            * faz nada e ainda impediria arrastar.
            */}
          <input ref={camera} type="file" accept={ACEITA} capture="environment" multiple hidden
            aria-label={`Fotografar a vertical ${vertical.codigo}`}
            onChange={(e) => {
              const lista = e.target.files ? Array.from(e.target.files) : [];
              e.target.value = '';
              void enviar(lista);
            }} />
          <ZonaDeArquivo rotulo="Enviar fotos" aceita={ACEITA} tipos="Imagens (JPEG, PNG, HEIC)" multiplo
            textoVazio="Arraste as fotos aqui" desabilitado={progresso !== null}
            aoEscolher={(lista) => { void enviar(lista); }} />
          <button type="button" className="botao secundario" disabled={progresso !== null}
            onClick={() => camera.current?.click()}>
            Fotografar agora
          </button>
          {progresso !== null && <BarraDeAvanco pct={progresso * 100} rotulo="Enviando as fotos" />}
        </div>
      )}

      {retiradas.length > 0 && (
        <details className="cptu-fotos-retiradas">
          <summary>{plural(retiradas.length, 'foto retirada', 'fotos retiradas')}</summary>
          <ul className="tf-lista">
            {retiradas.map((f) => (
              <li key={f.id}>
                <span className="mono">{f.arquivo}</span>
                {f.legenda && <span className="sub">{f.legenda}</span>}
                {editavel && (
                  <button type="button" className="ligacao" onClick={() => { void mudar(f, { ativa: true }); }}>
                    Devolver à vertical
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {vendo && <JanelaDaFoto foto={vendo} vertical={vertical.codigo} aoFechar={() => setVendo(null)} />}

      {tirando && (
        <JanelaDeTirar foto={tirando} vertical={vertical.codigo} aoFechar={() => setTirando(null)}
          aoTirar={async (motivo) => {
            /* A falha sobe para a janela, que fica aberta com o erro; o sucesso a fecha. */
            await aoMudar(await alterarFoto(tirando.id, { ativa: false, motivo }));
            setTirando(null);
          }} />
      )}
    </div>
  );
}

/**
 * A legenda grava ao sair do campo (ou no Enter), só se mudou. Numa caixa que quebra a linha: a legenda inteira à
 * vista, e não cortada na largura da miniatura (agent-browser da 312). A legenda é uma linha só — o Enter grava.
 */
function LegendaDaFoto({ foto, aoGravar }: { foto: FotoDaVertical; aoGravar(l: string | null): Promise<void> }) {
  const [texto, setTexto] = useState(foto.legenda ?? '');
  const gravar = () => {
    const nova = texto.replace(/\s+/g, ' ').trim() || null;
    if (nova !== (foto.legenda ?? null)) void aoGravar(nova);
  };
  return (
    <label className="campo cptu-foto-legenda-campo">
      <span className="so-leitor">Legenda da foto {foto.arquivo}</span>
      <textarea value={texto} maxLength={500} placeholder="Legenda" rows={2}
        onChange={(e) => setTexto(e.target.value.replace(/\n/g, ' '))} onBlur={gravar}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); gravar(); } }} />
    </label>
  );
}

/** A HEIC (e a HEIF) do iPhone: guardada como veio, e o navegador não a desenha — a janela só oferece baixar. */
const ehHeic = (f: FotoDaVertical) => /^image\/hei[cf]/i.test(f.mime ?? '') || /\.hei[cf]$/i.test(f.arquivo);

/**
 * A foto em HEIC marcada «no relatório» não vai ao PDF (onda 315, R7): o `sharp` da casa não decodifica HEIC, e o lugar
 * dela no PDF diz que o arquivo está guardado. O aviso aparece ao marcar, ao lado da caixa.
 */
function AvisoDoHeic({ foto }: { foto: FotoDaVertical }) {
  return (
    <p className="cptu-foto-aviso-heic" role="status">
      ▲ A imagem em HEIC não vai ao PDF do relatório (o arquivo «{foto.arquivo}» está guardado): envie em JPEG.
    </p>
  );
}

/**
 * A foto inteira, numa janela: na largura da janela e cabendo na tela, com «Baixar» (o arquivo exige sessão, então
 * os bytes vêm pelo `baixarBlob` e o endereço é local, solto ao fechar). O que o navegador não desenha — a HEIC, ou
 * a imagem que falha — fica só com o «Baixar».
 */
function JanelaDaFoto({ foto, vertical, aoFechar }: { foto: FotoDaVertical; vertical: string; aoFechar(): void }) {
  const [endereco, setEndereco] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [naoDesenha, setNaoDesenha] = useState(() => ehHeic(foto));

  useEffect(() => {
    const abortar = new AbortController();
    let url: string | null = null;
    baixarBlob(`/arquivos/${foto.arquivo_id}/conteudo`, abortar.signal)
      .then((b) => { url = URL.createObjectURL(b); setEndereco(url); })
      .catch((e) => { if ((e as Error).name !== 'AbortError') setErro(e); });
    return () => {
      abortar.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [foto.arquivo_id]);

  return (
    <Dialogo aberto larga titulo={foto.legenda ?? 'Foto do equipamento no ponto'} rotulo={`Vertical ${vertical}`}
      aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      {!endereco && !erro && <Carregando texto="Abrindo a foto" />}
      {endereco && !naoDesenha && (
        <img className="cptu-foto-grande" src={endereco} alt={foto.legenda ?? `foto da vertical ${vertical}`}
          onError={() => setNaoDesenha(true)} />
      )}
      {naoDesenha && (
        <p className="sub">
          O navegador não abre este formato (<span className="mono">{foto.arquivo}</span>): baixe a foto, guardada como
          veio, para vê-la.
        </p>
      )}
      <div className="rodape-dialogo">
        {endereco
          ? <a className="botao" href={endereco} download={foto.arquivo}>Baixar</a>
          : <button type="button" className="botao" aria-disabled="true" title="Esperando o arquivo">Baixar</button>}
        <button type="button" className="botao secundario" onClick={aoFechar}>Fechar</button>
      </div>
    </Dialogo>
  );
}

/**
 * Tirar com motivo: a foto não se apaga (o banco recusa), e volta por
 * «Devolver à vertical». A recusa da API fica NA janela, com o motivo
 * escrito: a janela só fecha quando a foto saiu.
 */
function JanelaDeTirar({ foto, vertical, aoFechar, aoTirar }: {
  foto: FotoDaVertical; vertical: string; aoFechar(): void; aoTirar(motivo: string): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres).' : null;
  const tirar = async () => {
    if (falta || ocupado) return;
    setOcupado(true); setErro(null);
    try { await aoTirar(motivo.trim()); } catch (e) { setErro(e); setOcupado(false); }
  };
  return (
    <Dialogo aberto titulo="Tirar a foto da vertical" rotulo={`Vertical ${vertical}`} aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p className="sub">
        A foto «<span className="mono">{foto.arquivo}</span>» sai da vertical e do relatório, mas não se apaga:
        ela fica em «Fotos retiradas», de onde volta.
      </p>
      <Area rotulo="Motivo" rows={2} value={motivo} maxLength={2000}
        dica="Pelo menos 10 caracteres: o motivo vai para a trilha." onChange={(e) => setMotivo(e.target.value)} />
      {falta && <p className="cptu-falta" role="status">{falta}</p>}
      <div className="rodape-dialogo">
        <button type="button" className="botao" aria-disabled={!!falta || ocupado}
          onClick={() => { void tirar(); }}>
          {ocupado ? 'Tirando…' : 'Tirar'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
      </div>
    </Dialogo>
  );
}
